<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { UUID } from '@hfc/common/src/types/UUID';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import ProgressBar from '@/application/shared/infrastructure/components/ProgressBar.vue';
import AppNavigation from '@/application/shared/infrastructure/components/AppNavigation.vue';
import { LinkButton, useRoute } from '@hfc/ui-core';
import SaveAndExitModal from '@/application/shared/infrastructure/components/SaveAndExitModal.vue';
import { ApplicationSection } from '@/shared/application/ApplicationSection';
import { QuoteInterface } from '@/quote/domain/QuoteInterface';
import { useSectionNavigation } from '@/shared/infrastructure/composition/SectionNavigation';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import SectionIntro from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionIntro.vue';
import SectionConsentOfVerification from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionConsentOfVerification.vue';
import SectionApplicantInfo from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionApplicantInfo.vue';
import SectionAddress from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionAddress.vue';
import SectionDependentNames from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionDependentNames.vue';
import SectionDependentAddress from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionDependentAddress.vue';
import SectionGuardian from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionGuardian.vue';
import SectionReview from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionReview.vue';
import SectionLegalAgreement from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionLegalAgreement.vue';
import SectionSignature from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionSignature.vue';
import SectionThanks from '@/application/shared/infrastructure/components/DentalAndVisionSections/SectionThanks.vue';
import ClearAndRestartModal from '@/shared/infrastructure/components/ClearAndRestartModal.vue';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';
import { usePlanStore } from '@/planList/infrastructure/composition/usePlanStore';

const { appStore } = useAppStore();
const { quoteStore, quoteState } = useQuoteStore();
const { planStore } = usePlanStore();
const route = useRoute();

const genericProps = {
  applicationType: APPLICATION_TYPES.DENTAL
};

const {
  dentalAndVisionApplicationStore,
  shouldLoadApplicationFromQuote,
  showSectionDependentAddresses,
  showSectionDependentNames,
  isChildOnlyApplication,
  showLastStepInFlow,
  clearAndRestartApplication,
  shouldLoadApplicationFromLocalStorage,
  submitApplication,
  saveApplicationAndContinue,
  primaryApplicant,
  application
} = useDentalAndVisionApplication(APPLICATION_TYPES.DENTAL, route.params.uuid as UUID, planStore, quoteStore);

// we imply section order from this array
const sectionList: Array<ApplicationSection> = [{
  component: SectionIntro,
  shortTitle: 'Agreement'
}, {
  component: SectionConsentOfVerification,
  shortTitle: 'Consent',
  hideInProgressBar: true,
  props: { ...genericProps }
}, {
  component: SectionApplicantInfo,
  shortTitle: 'Applicant Info',
  props: { ...genericProps }
}, {
  component: SectionAddress,
  shortTitle: 'Address',
  hideInProgressBar: true,
  props: { ...genericProps }
}, {
  component: SectionDependentNames,
  conditionalShow: (): boolean => showSectionDependentNames.value,
  shortTitle: 'Dependents',
  props: { ...genericProps }
}, {
  component: SectionDependentAddress,
  conditionalShow: (): boolean => showSectionDependentAddresses.value,
  shortTitle: 'Addresses',
  hideInProgressBar: true,
  props: { ...genericProps }
}, {
  component: SectionGuardian,
  conditionalShow: (): boolean => isChildOnlyApplication.value,
  shortTitle: 'Guardian',
  props: { ...genericProps }
}, {
  component: SectionReview,
  shortTitle: 'Review',
  props: { ...genericProps }
}, {
  component: SectionLegalAgreement,
  shortTitle: 'Agreement',
  customContinueText: 'I agree',
  hideInProgressBar: true
}, {
  component: SectionSignature,
  shortTitle: 'Signature',
  customContinueText: 'Submit Application',
  props: { ...genericProps }
}, {
  component: SectionThanks,
  shortTitle: 'Payment'
}];

const stepRef = ref<InstanceType<typeof SectionIntro> | null>(null);
const loading = ref(false);
const apiError = ref(false);
const showSaveAndExitModal = ref(false);

const {
  showProgressBar,
  currentSection,
  filteredSectionList,
  nextSection,
  prevSection,
  goToSection
} = useSectionNavigation(sectionList, stepRef, showLastStepInFlow);

async function submitForm (): Promise<void> {
  loading.value = true;
  const result = await submitApplication(planStore, quoteStore);
  if (!result) {
    // Re enable only on error, success means we are getting redirected
    loading.value = false;
    apiError.value = true;
  }
}

onMounted(async () => {
  await appStore.updateCustomTitle('Dental Application');
  await appStore.updateCustomTitleClass('lg:mr-[220px]');
  await appStore.setShowFooter(false);

  if (shouldLoadApplicationFromLocalStorage()) {
    await dentalAndVisionApplicationStore.initAppFromLocalStorage(APPLICATION_TYPES.DENTAL);
  } else if (await shouldLoadApplicationFromQuote()) {
    await quoteStore.getQuoteInfo(APPLICATION_TYPES.DENTAL);
    const quote = quoteState.value[APPLICATION_TYPES.DENTAL] as QuoteInterface;
    const plan = planStore.state.selectedDentalPlan;
    dentalAndVisionApplicationStore.initAppFromQuote(quote, plan, APPLICATION_TYPES.DENTAL);
  }
});

const checkSaveAndExitInfo = async (email: string): Promise<void> => {
  if (email !== primaryApplicant.value.email) {
    dentalAndVisionApplicationStore.updateApplicant({ email: email, confirm_email: email }, APPLICATION_TYPES.DENTAL, primaryApplicant.value.main_index);
    dentalAndVisionApplicationStore.saveApplicationOnLocalStorage(application.value, APPLICATION_TYPES.DENTAL);
  }
};

onBeforeUnmount(async () => {
  await appStore.updateCustomTitle('');
  await appStore.updateCustomTitleClass('');
  await appStore.setShowFooter(true);
});

watch(() => currentSection.value, () => {
  apiError.value = false;
});
</script>

<template>
  <div class="flex items-start flex-col flex-grow mb-3">
    <div class="py-3 w-full">
      <ProgressBar :current-section="currentSection" v-if="showProgressBar" :show-last-step-in-flow="showLastStepInFlow" :section-list="filteredSectionList" @goToSection="goToSection"></ProgressBar>
    </div>
    <div class="flex-grow flex flex-col w-full">
      <div class="w-full flex flex-col">
        <component :is="filteredSectionList[currentSection].component" ref="stepRef" v-bind="filteredSectionList[currentSection].props" @prevSection="prevSection" @nextSection="() => saveApplicationAndContinue(nextSection)" @setLoading="(v: boolean) => loading = v" @goToSection="goToSection" @submit-form="submitForm">
        </component>
        <p v-if="apiError" class="error text-right px-4 md:px-7">There was an error</p>
      </div>
      <AppNavigation :current-step="currentSection" :total-steps="filteredSectionList.length - 1" :show-last-step-in-flow="showLastStepInFlow" @prevSection="prevSection" @nextSection="() => saveApplicationAndContinue(nextSection)" @submit="submitForm" :loading="loading" :continue-text="filteredSectionList[currentSection].customContinueText">
        <template #after-continue>
          <div class="flex flex-col">
            <LinkButton @click="showSaveAndExitModal = true">Save and Exit</LinkButton>
            <SaveAndExitModal v-if="showSaveAndExitModal" @close="showSaveAndExitModal = false" :email="primaryApplicant.email" :application-type="genericProps.applicationType" :uuid="application.uuid" :application="application" :callback="checkSaveAndExitInfo" />
            <ClearAndRestartModal @confirm="clearAndRestartApplication(planStore, quoteStore)" />
          </div>
        </template>
      </AppNavigation>
    </div>
  </div>
</template>
